// PAY2-S8 (docs/build-briefs/PAY2-S8-royalty-adjustments-disputes-views-exports.md; DEC-405, DEC-406, DEC-420): the pure
// decisions behind the location's Royalty charges panel and the franchisor's Charges and Disputes cards. No DOM, no fetch.
// The server does every calculation (the floor, the minimum, the carry); this module reads its answer, checks its shape,
// labels and formats cents. An empty or absent value reads N/A.

import { formatCents } from "./money.ts";

export const CHARGES_NA = "-";
export const CHARGES_FAILED = "The charges could not be loaded. Try again.";
export const CHARGES_DOWNLOAD_FAILED = "The file could not be downloaded. Try again.";
export const DISPUTE_FAILED = "The dispute could not be filed. Try again.";
export const RESOLVE_FAILED = "The dispute could not be resolved. Try again.";
export const DISPUTE_NOTE_MAX = 500;

/** The reasons a location can give; the server holds the same list and refuses any other. */
export const DISPUTE_REASON_OPTIONS: readonly { code: string; label: string }[] = [
  { code: "amount_incorrect", label: "Amount is incorrect" },
  { code: "not_authorized", label: "Debit not authorized" },
  { code: "already_paid", label: "Already paid" },
  { code: "other", label: "Other" },
];

export type Tone = "good" | "warn" | "bad" | "neutral";
const TONES: readonly string[] = ["good", "warn", "bad", "neutral"];

export interface DisputeRef {
  id: string;
  status: string;
  statusLabel: string;
}

export interface LedgerChargeDto {
  id: string;
  month: string;
  status: string;
  label: string;
  tone: Tone;
  royaltyDueCents: number | null;
  /** FRADFUND1: the fund fee inside the amount, its own line. Absent from an older server. */
  fundDueCents?: number | null;
  adjustmentCents: number;
  amountCents: number | null;
  expectedDebitDate: string | null;
  attempts: number;
  dispute: DisputeRef | null;
  canDispute: boolean;
}

export interface LedgerAdjustmentDto {
  id: string;
  month: string;
  kind: string;
  kindLabel: string;
  deltaCents: number;
  appliedCents: number;
  remainingCents: number;
  nettedIn: string[];
  createdAt: string;
}

export interface LedgerStatementDto {
  month: string;
  version: number;
  royaltyDueCents: number;
  netCents: number;
  takenAt: string;
}

export interface LedgerDisputeDto {
  id: string;
  chargeId: string;
  month: string;
  status: string;
  statusLabel: string;
  reasonCode: string;
  reasonLabel: string;
  note: string | null;
  openedAt: string;
  resolvedAt: string | null;
  resolutionNote: string | null;
  creditCents: number | null;
}

export interface LocationLedgerDto {
  locationName: string;
  charges: LedgerChargeDto[];
  adjustments: LedgerAdjustmentDto[];
  statements: LedgerStatementDto[];
  disputes: LedgerDisputeDto[];
  pendingCents: number;
}

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null;
const isNum = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);
const isNumOrNull = (v: unknown): boolean => v === null || isNum(v);
const isStrOrNull = (v: unknown): boolean => v === null || typeof v === "string";

function isCharge(v: unknown): boolean {
  return (
    isObject(v) &&
    typeof v["id"] === "string" &&
    typeof v["month"] === "string" &&
    typeof v["status"] === "string" &&
    typeof v["label"] === "string" &&
    typeof v["tone"] === "string" &&
    TONES.includes(v["tone"]) &&
    isNumOrNull(v["royaltyDueCents"]) &&
    isNum(v["adjustmentCents"]) &&
    isNumOrNull(v["amountCents"]) &&
    isStrOrNull(v["expectedDebitDate"]) &&
    typeof v["canDispute"] === "boolean" &&
    (v["dispute"] === null ||
      (isObject(v["dispute"]) && typeof v["dispute"]["statusLabel"] === "string"))
  );
}

const isAdjustment = (v: unknown): boolean =>
  isObject(v) &&
  typeof v["month"] === "string" &&
  typeof v["kindLabel"] === "string" &&
  isNum(v["deltaCents"]) &&
  isNum(v["appliedCents"]) &&
  isNum(v["remainingCents"]) &&
  Array.isArray(v["nettedIn"]);

const isStatementRow = (v: unknown): boolean =>
  isObject(v) &&
  typeof v["month"] === "string" &&
  isNum(v["version"]) &&
  isNum(v["royaltyDueCents"]);

const isDispute = (v: unknown): boolean =>
  isObject(v) &&
  typeof v["id"] === "string" &&
  typeof v["month"] === "string" &&
  typeof v["statusLabel"] === "string" &&
  typeof v["reasonLabel"] === "string" &&
  typeof v["openedAt"] === "string" &&
  isNumOrNull(v["creditCents"]);

export function isLedgerShape(value: unknown): value is LocationLedgerDto {
  return (
    isObject(value) &&
    typeof value["locationName"] === "string" &&
    Array.isArray(value["charges"]) &&
    value["charges"].every(isCharge) &&
    Array.isArray(value["adjustments"]) &&
    value["adjustments"].every(isAdjustment) &&
    Array.isArray(value["statements"]) &&
    value["statements"].every(isStatementRow) &&
    Array.isArray(value["disputes"]) &&
    value["disputes"].every(isDispute) &&
    isNum(value["pendingCents"])
  );
}

export interface NetworkChargeRowDto {
  franchisee: string;
  locationName: string;
  status: string | null;
  label: string | null;
  tone: Tone;
  royaltyDueCents: number | null;
  fundDueCents?: number | null;
  adjustmentCents: number;
  amountCents: number | null;
  expectedDebitDate: string | null;
  pendingCents: number;
  dispute: DisputeRef | null;
}

export interface NetworkLedgerDto {
  month: string;
  from: string;
  to: string;
  rows: NetworkChargeRowDto[];
  adjustments: {
    franchisee: string;
    locationName: string;
    month: string;
    kindLabel: string;
    deltaCents: number;
    appliedCents: number;
    remainingCents: number;
  }[];
  /** Every dispute of the network with its reason and note (PAY2-S9 reads the one a row names). */
  disputes?: NetworkDisputeDto[];
}

export function isNetworkLedgerShape(value: unknown): value is NetworkLedgerDto {
  return (
    isObject(value) &&
    typeof value["month"] === "string" &&
    /^\d{4}-\d{2}$/.test(value["month"]) &&
    typeof value["from"] === "string" &&
    typeof value["to"] === "string" &&
    Array.isArray(value["rows"]) &&
    value["rows"].every(
      (r) =>
        isObject(r) &&
        typeof r["franchisee"] === "string" &&
        typeof r["locationName"] === "string" &&
        isNumOrNull(r["royaltyDueCents"]) &&
        isNum(r["adjustmentCents"]) &&
        isNumOrNull(r["amountCents"]) &&
        isNum(r["pendingCents"]) &&
        isStrOrNull(r["label"]),
    ) &&
    Array.isArray(value["adjustments"])
  );
}

export interface NetworkDisputeDto {
  id: string;
  franchisee: string;
  locationName: string;
  month: string;
  chargeAmountCents: number | null;
  status: string;
  statusLabel: string;
  reasonCode: string;
  reasonLabel: string;
  note: string | null;
  openedAt: string;
  resolvedAt: string | null;
  resolutionNote: string | null;
  creditCents: number | null;
}

export interface DisputeListDto {
  canResolve: boolean;
  disputes: NetworkDisputeDto[];
}

export function isDisputeListShape(value: unknown): value is DisputeListDto {
  return (
    isObject(value) &&
    typeof value["canResolve"] === "boolean" &&
    Array.isArray(value["disputes"]) &&
    value["disputes"].every(
      (d) =>
        isObject(d) &&
        typeof d["id"] === "string" &&
        typeof d["franchisee"] === "string" &&
        typeof d["locationName"] === "string" &&
        typeof d["month"] === "string" &&
        typeof d["status"] === "string" &&
        typeof d["statusLabel"] === "string" &&
        typeof d["reasonLabel"] === "string" &&
        isNumOrNull(d["chargeAmountCents"]) &&
        isNumOrNull(d["creditCents"]),
    )
  );
}

/** Money for a cell: signed cents as "$12.00" or "-$5.00"; absent is N/A. */
export function centsCell(cents: number | null): string {
  return cents === null ? CHARGES_NA : formatCents(cents);
}

/** An adjustment shows its sign: "+$10.00" or "-$5.00". */
export function signedCentsCell(cents: number | null): string {
  if (cents === null) return CHARGES_NA;
  return cents > 0 ? `+${formatCents(cents)}` : formatCents(cents);
}

/** A balance: zero reads $0.00, any other amount keeps its sign. */
export function balanceCell(cents: number): string {
  return cents === 0 ? formatCents(0) : signedCentsCell(cents);
}

/** "2026-10-09" or "2026-10-09T14:00:00.000Z" as "Oct 9, 2026"; anything else reads N/A. */
export function dayCell(value: string | null): string {
  if (value === null) return CHARGES_NA;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (match === null) return CHARGES_NA;
  const names = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const name = names[Number(match[2]) - 1];
  return name === undefined ? CHARGES_NA : `${name} ${Number(match[3])}, ${match[1]}`;
}

export function textCell(value: string | null): string {
  return value === null || value === "" ? CHARGES_NA : value;
}

export function toneOf(value: string): Tone {
  return (TONES.includes(value) ? value : "neutral") as Tone;
}

/** The dispute form's request body, or the plain reason it cannot be sent. */
export function disputeRequest(
  reason: string,
  note: string,
): { ok: true; body: { reason: string; note?: string } } | { ok: false; message: string } {
  if (!DISPUTE_REASON_OPTIONS.some((o) => o.code === reason))
    return { ok: false, message: "Choose a reason." };
  const trimmed = note.trim();
  if (trimmed.length > DISPUTE_NOTE_MAX)
    return { ok: false, message: `The note is at most ${DISPUTE_NOTE_MAX} characters.` };
  return { ok: true, body: trimmed === "" ? { reason } : { reason, note: trimmed } };
}

/** The resolve form's request body from what the person typed, or the plain reason it cannot be sent. The credit is typed in
 * dollars and cents and sent in whole cents. */
export function resolveRequest(
  resolution: string,
  creditDollars: string,
  note: string,
  parseCents: (raw: string) => number | null,
):
  | { ok: true; body: { resolution: string; creditCents?: number; note: string } }
  | { ok: false; message: string } {
  if (resolution !== "upheld" && resolution !== "adjusted")
    return { ok: false, message: "Choose upheld or adjusted." };
  const trimmed = note.trim();
  if (trimmed === "") return { ok: false, message: "Add a note." };
  if (trimmed.length > DISPUTE_NOTE_MAX)
    return { ok: false, message: `The note is at most ${DISPUTE_NOTE_MAX} characters.` };
  if (resolution === "upheld") return { ok: true, body: { resolution, note: trimmed } };
  const cents = parseCents(creditDollars);
  if (cents === null || cents < 1)
    return { ok: false, message: "Enter the credit in dollars and cents." };
  return { ok: true, body: { resolution, creditCents: cents, note: trimmed } };
}
